import { Link, useLocation } from 'react-router-dom'
import {
  LayoutDashboard, BookOpen, Building2, Users, FileQuestion,
  Layers, GraduationCap, BarChart3, BookMarked, CalendarDays, Tag, Mail,
} from 'lucide-react'
import { useRole } from '@/hooks/useRole'
import { ROUTES } from '@/config/routes'
import { clsx } from 'clsx'

interface NavItem {
  label: string
  to: string
  icon: React.ReactNode
}

/** Collapsible left-hand navigation sidebar. Shows role-filtered nav links and a collapse toggle. */
export function Sidebar({ collapsed }: { collapsed: boolean }) {
  const { isSuperUser, isManager, isTutor, isStudent } = useRole()
  const { pathname } = useLocation()

  const superuserLinks: NavItem[] = [
    { label: 'Dashboard',          to: ROUTES.ADMIN.DASHBOARD,          icon: <LayoutDashboard className="h-5 w-5" /> },
    { label: 'Users',              to: ROUTES.ADMIN.USERS,              icon: <Users className="h-5 w-5" /> },
    { label: 'Invitations',        to: ROUTES.ADMIN.INVITATIONS,        icon: <Mail className="h-5 w-5" /> },
    { label: 'Institutes',         to: ROUTES.ADMIN.INSTITUTES,         icon: <Building2 className="h-5 w-5" /> },
    { label: 'Units',              to: ROUTES.ADMIN.UNITS,              icon: <BookOpen className="h-5 w-5" /> },
    { label: 'Questions',          to: ROUTES.QUESTIONS.LIST,           icon: <FileQuestion className="h-5 w-5" /> },
    { label: 'Answers',            to: ROUTES.ANSWERS.LIST,             icon: <Layers className="h-5 w-5" /> },
    { label: 'Tests',              to: ROUTES.ADMIN.TESTS,              icon: <BookMarked className="h-5 w-5" /> },
    { label: 'Classes',            to: ROUTES.CLASSES.LIST,             icon: <CalendarDays className="h-5 w-5" /> },
    { label: 'Tags',               to: ROUTES.ADMIN.TAGS,               icon: <Tag className="h-5 w-5" /> },
    { label: 'Analytics',          to: ROUTES.ADMIN.ANALYTICS,          icon: <BarChart3 className="h-5 w-5" /> },
  ]

  const managerLinks: NavItem[] = [
    { label: 'Dashboard',          to: ROUTES.ADMIN.DASHBOARD,          icon: <LayoutDashboard className="h-5 w-5" /> },
    { label: 'Institutes',         to: ROUTES.INSTITUTE.LIST,           icon: <Building2 className="h-5 w-5" /> },
    { label: 'Units',              to: ROUTES.UNIT.LIST,                icon: <BookOpen className="h-5 w-5" /> },
    { label: 'Questions',          to: ROUTES.QUESTIONS.LIST,           icon: <FileQuestion className="h-5 w-5" /> },
    { label: 'Answers',            to: ROUTES.ANSWERS.LIST,             icon: <Layers className="h-5 w-5" /> },
    { label: 'Tests',              to: ROUTES.TESTS.LIST,               icon: <BookMarked className="h-5 w-5" /> },
    { label: 'Classes',            to: ROUTES.CLASSES.LIST,             icon: <CalendarDays className="h-5 w-5" /> },
    { label: 'Tags',               to: ROUTES.TAGS.LIST,                icon: <Tag className="h-5 w-5" /> },
  ]

  const tutorLinks: NavItem[] = [
    { label: 'Dashboard',          to: ROUTES.ADMIN.DASHBOARD,          icon: <LayoutDashboard className="h-5 w-5" /> },
    { label: 'Units',              to: ROUTES.UNIT.LIST,                icon: <BookOpen className="h-5 w-5" /> },
    { label: 'Institutes',         to: ROUTES.INSTITUTE.LIST,           icon: <Building2 className="h-5 w-5" /> },
    { label: 'Questions',          to: ROUTES.QUESTIONS.LIST,           icon: <FileQuestion className="h-5 w-5" /> },
    { label: 'Answers',            to: ROUTES.ANSWERS.LIST,             icon: <Layers className="h-5 w-5" /> },
    { label: 'Tests',              to: ROUTES.TESTS.LIST,               icon: <BookMarked className="h-5 w-5" /> },
    { label: 'Classes',            to: ROUTES.CLASSES.LIST,             icon: <CalendarDays className="h-5 w-5" /> },
    { label: 'Tags',               to: ROUTES.TAGS.LIST,                icon: <Tag className="h-5 w-5" /> },
    { label: 'Users',              to: ROUTES.ADMIN.USERS,              icon: <Users className="h-5 w-5" /> },
  ]

  const studentLinks: NavItem[] = [
    { label: 'Dashboard',  to: ROUTES.STUDENT.DASHBOARD,  icon: <LayoutDashboard className="h-5 w-5" /> },
    { label: 'Enrollment', to: ROUTES.STUDENT.ENROLLMENT, icon: <GraduationCap className="h-5 w-5" /> },
    { label: 'History',    to: ROUTES.STUDENT.HISTORY,    icon: <BarChart3 className="h-5 w-5" /> },
  ]

  const links = isSuperUser
    ? superuserLinks
    : isManager
    ? managerLinks
    : isTutor
    ? tutorLinks
    : isStudent
    ? studentLinks
    : []

  return (
    <aside
      className={clsx(
        'flex h-full flex-col border-r border-gray-200 bg-white transition-all duration-200',
        collapsed ? 'w-16' : 'w-56'
      )}
    >
      <nav className="flex-1 overflow-y-auto p-2">
        {links.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            className={clsx(
              'mb-0.5 flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              pathname.startsWith(item.to)
                ? 'bg-primary-50 text-primary-700'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            )}
            title={collapsed ? item.label : undefined}
          >
            {item.icon}
            {!collapsed && <span>{item.label}</span>}
          </Link>
        ))}
      </nav>
    </aside>
  )
}

